import React, { useMemo, useState, useEffect } from 'react';
import { Box, VStack, HStack, Text, SimpleGrid, createListCollection } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../../hooks/useTranslation';
import { useGetCustomerQuery } from '../../api';
import { useRooms } from '../../hooks/useRooms';
import PageHeader from '../../components/base/PageHeader';
import LoadingState from '../../components/base/LoadingState';
import MeetingRoomCard from '../../components/office/MeetingRoomCard';
import BaseSelect from '../../components/base/BaseSelect';
import { RoomKind } from '../../api/rooms/types';
import { canPreviewCoworking } from './coworkingPreview';

interface MeetingRoomsPageProps {
    roomType?: RoomKind;
}

const MeetingRoomsPage: React.FC<MeetingRoomsPageProps> = ({ roomType = 'conference' }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { data: customer, isLoading: isCustomerLoading } = useGetCustomerQuery(undefined, {
        skip: roomType !== 'coworking',
    });
    const { rooms, isLoading } = useRooms(roomType);
    const isCoworking = roomType === 'coworking';
    const coworkingAllowed = !isCoworking || canPreviewCoworking(customer?.id);

    useEffect(() => {
        if (isCoworking && customer && !canPreviewCoworking(customer.id)) {
            navigate('/office', { replace: true });
        }
    }, [isCoworking, customer, navigate]);
    const listPath = isCoworking ? '/office/coworking' : '/office/meeting-rooms';
    const title = isCoworking ? t.pages.office.coworking : t.pages.office.meetingRooms;
    const emptyMessage = isCoworking ? t.pages.office.noCoworkingAvailable : t.pages.office.noRoomsAvailable;
    const [selectedLocation, setSelectedLocation] = useState<string>('all');

    // Получаем уникальные локации из всех переговорных
    const locations = useMemo(() => {
        if (!rooms) return [];
        const uniqueLocations = Array.from(new Set(rooms.map((room) => room.location).filter(Boolean)));
        return uniqueLocations.sort();
    }, [rooms]);

    // Устанавливаем первую локацию по умолчанию
    useEffect(() => {
        if (locations.length > 0 && selectedLocation === 'all') {
            setSelectedLocation(locations[0]);
        }
    }, [locations, selectedLocation]);

    // Фильтруем переговорные по выбранной локации
    const filteredRooms = useMemo(() => {
        if (!rooms) return [];
        if (selectedLocation === 'all') return rooms;
        return rooms.filter((room) => room.location === selectedLocation && room.active);
    }, [rooms, selectedLocation]);

    // Создаем коллекцию для селектора
    const locationCollection = useMemo(() => {
        return createListCollection({
            items: [
                { value: 'all', label: t.pages.office.allLocations },
                ...locations.map((location) => ({ value: location, label: location })),
            ],
        });
    }, [locations, t.pages.office.allLocations]);

    if (isLoading || (isCoworking && (isCustomerLoading || !coworkingAllowed))) {
        return <LoadingState message={t.pages.support.loading} />;
    }

    return (
        <Box h="calc(100vh - 20px)" overflow="auto" bg="gray.100" pt={2} pb={{ base: 20, lg: 6 }}>
            <PageHeader title={title} backPath="/office" />
            <Box maxW="6xl" mx="auto" p={4}>
                <VStack gap={6} align="stretch" w={{ base: 'full', md: '600px' }} mx="auto">
                    {/* Селектор локации */}
                    {locations.length > 1 && (
                        <Box>
                            <BaseSelect
                                collection={locationCollection}
                                value={selectedLocation}
                                onValueChange={(value) => setSelectedLocation(value || 'all')}
                                placeholder={t.pages.office.selectLocation}
                                minW="200px"
                                maxW={{ base: 'full', md: '200px' }}
                            />
                        </Box>
                    )}

                    {/* Список карточек переговорных */}
                    {filteredRooms.length > 0 ? (
                        <SimpleGrid columns={{ base: 1, md: 1, lg: 1 }} gap={6}>
                            {filteredRooms.map((room) => (
                                <MeetingRoomCard
                                    key={room.id}
                                    room={room}
                                    bookingPath={`${listPath}/booking`}
                                />
                            ))}
                        </SimpleGrid>
                    ) : (
                        <Box p={8} bg="white" borderRadius="lg" textAlign="center">
                            <Text fontSize="lg" color="gray.600">
                                {emptyMessage}
                            </Text>
                        </Box>
                    )}
                </VStack>
            </Box>
        </Box>
    );
};

export default MeetingRoomsPage;
